import {
  Box,
  Button,
  Center,
  HStack,
  Input,
  ListItem,
  Spinner,
  Text,
  UnorderedList,
} from '@chakra-ui/react';
import { useDebounce } from 'use-debounce';
import { trpcTenant } from '@duke-hq/app-provider-web/tenant/trpcTenant';
import { Trans } from '@lingui/react/macro';
import { ChevronRightIcon, ChevronLeftIcon } from 'lucide-react';
import { ChangeEvent, useCallback, useState } from 'react';
import { useSnapshot } from 'valtio';
import { courseReferralModalState } from '../../tenantState/CourseReferralState';
import {
  FormContentContainer,
  FormFooterContainer,
  FormMainContainer,
} from '@duke-hq/ui/components/Modals/FormModalLayouts';
import type { RouterOutputs } from '@duke-hq/svc-api/provider';
import { CourseKindLabels } from '@duke-hq/app-provider-web/tenant/Courses/constants';
import { CourseKindSchema } from '@duke-hq/svc-api-db/schemas/enums/CourseKind.schema';
import { CourseSegment, WorkSchedule } from '@duke-hq/shared-constants/enrolments';
import { overflowScrollbarStyles } from '@duke-hq/ui/styles/overflowScrollbarStyles';

export function SelectCourse({
  goToNextStep,
  goToPrevStep,
}: {
  goToNextStep: () => void;
  goToPrevStep: () => void;
}) {
  const [search, setSearch] = useState<string>('');
  const [debouncedSearch] = useDebounce(search, 500);
  const snap = useSnapshot(courseReferralModalState);
  const { data: coursesList, isLoading } = trpcTenant.courses.listWithMinimalAttributes.useQuery({
    search: debouncedSearch.trim() === '' ? undefined : debouncedSearch.trim(),
  });

  const handleInputChange = (e: ChangeEvent<HTMLInputElement>) => {
    const { value } = e.target;
    setSearch(value);
  };

  return (
    <FormMainContainer>
      <FormContentContainer>
        <Input
          autoComplete="off"
          autoFocus
          fontSize="sm"
          minH="8"
          maxH="8"
          onChange={handleInputChange}
          placeholder="Search"
          size="sm"
          type="text"
          value={search}
          width="full"
        />
        {isLoading ? (
          <Center h="full" w="full">
            <Spinner color="border.accent" size="lg" />
          </Center>
        ) : (
          <Box maxH="450px" overflowY="auto" w="full" pr={2} css={overflowScrollbarStyles}>
            <CourseList courses={coursesList} />
          </Box>
        )}
      </FormContentContainer>
      <FormFooterContainer>
        <Button
          rightIcon={<ChevronRightIcon />}
          onClick={goToNextStep}
          isDisabled={!snap.selectedCourseAttributes.id}
        >
          <Trans>Next</Trans>
        </Button>
        <Button onClick={goToPrevStep} variant="tertiary" leftIcon={<ChevronLeftIcon />}>
          <Trans>Back</Trans>
        </Button>
      </FormFooterContainer>
    </FormMainContainer>
  );
}

function CourseList({
  courses,
}: {
  courses: RouterOutputs['courses']['listWithMinimalAttributes'] | undefined;
}) {
  const snap = useSnapshot(courseReferralModalState);

  const onSelectCourse = useCallback(
    (course: RouterOutputs['courses']['listWithMinimalAttributes'][number]) => {
      courseReferralModalState.setSelectedCourseAttributes({
        id: course.id,
        identifier: course.identifier ?? '',
        title: course.title,
        specialisation: course.specialisation ?? '',
        price_aud: Number(course.price_aud),
        kind: CourseKindSchema.parse(course.kind),
        status: course.status,
      });

      // Reset enrolment state when course is changed
      courseReferralModalState.enrolmentSettingsData.course_segment = CourseSegment.FULL_COURSE;
      courseReferralModalState.enrolmentSettingsData.contract_category = '';
      courseReferralModalState.enrolmentSettingsData.enrolment_contract_type = '';
      courseReferralModalState.enrolmentSettingsData.work_schedule = WorkSchedule.FULL_TIME;
    },
    [],
  );

  if (!courses) {
    return (
      <Box w="full">
        <Text color="fg.muted" textAlign="left" textStyle="sm">
          <Trans>Sorry, we couldn't load courses. Please refresh or try again later.</Trans>
        </Text>
      </Box>
    );
  }

  if (courses.length === 0) {
    return (
      <Box w="full">
        <Text color="fg.muted" textAlign="left" textStyle="sm">
          <Trans>No courses found.</Trans>
        </Text>
      </Box>
    );
  }

  return (
    <UnorderedList
      styleType="none"
      w="full"
      p="0"
      m="0"
      border="1px solid"
      borderRadius="8px"
      borderColor="border.emphasis"
      sx={{
        'li:not(:last-child)': {
          borderBottom: '1px solid',
          borderColor: 'border.emphasis',
        },
        'li:first-of-type': {
          borderTopLeftRadius: '8px',
          borderTopRightRadius: '8px',
        },
        'li:last-of-type': {
          borderBottomLeftRadius: '8px',
          borderBottomRightRadius: '8px',
        },
      }}
    >
      {courses?.map((course) => (
        <ListItem
          key={course.id}
          onClick={() => onSelectCourse(course)}
          p="8px 16px"
          bg={snap.selectedCourseAttributes.id === course.id ? 'bg.muted' : 'transparent'}
          cursor="pointer"
          _hover={{
            bg: snap.selectedCourseAttributes.id === course.id ? 'bg.muted' : 'bg.subtle',
          }}
        >
          <HStack justify="start">
            <Text color="fg.subtle" fontSize="sm" lineHeight="5">
              {course.identifier}
            </Text>
            {course.specialisation != null ? (
              <Text color="fg.emphasis" fontSize="sm" lineHeight="5" fontWeight="bold">
                {course.title}
              </Text>
            ) : null}
          </HStack>
          {course.specialisation != null ? (
            <Text color="fg.subtle" fontSize="xs" lineHeight="5">
              {course.specialisation}
            </Text>
          ) : (
            <Text color="fg.emphasis" fontSize="sm" lineHeight="5" fontWeight="bold">
              <Box as="span">
                {course.kind && CourseKindLabels[course.kind] != null ? (
                  <Box as="span">{CourseKindLabels[course.kind]}: </Box>
                ) : null}
                <Box as="span">{course.title}</Box>
              </Box>
            </Text>
          )}
        </ListItem>
      ))}
    </UnorderedList>
  );
}
